import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import {
  Avatar,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from "@material-ui/core";
import { Pencil, Trash2, Users as UsersIcon } from "lucide-react";

import MainContainer from "../../components/MainContainer";
import whatsappIcon from "../../assets/nopicture.png";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import UserModal from "../../components/UserModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import UserStatusIcon from "../../components/UserModal/statusIcon";
import { getBackendUrl } from "../../config";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useSearch } from "../../context/Search/SearchContext";
import ForbiddenPage from "../../components/ForbiddenPage";

const backendUrl = getBackendUrl();

const reducer = (state, action) => {
  if (action.type === "LOAD_USERS") {
    const users = action.payload;
    const newUsers = [];

    users.forEach((user) => {
      const userIndex = state.findIndex((u) => u.id === user.id);
      if (userIndex !== -1) {
        state[userIndex] = user;
      } else {
        newUsers.push(user);
      }
    });

    return [...state, ...newUsers];
  }

  if (action.type === "UPDATE_USERS") {
    const user = action.payload;
    const userIndex = state.findIndex((u) => u.id === user.id);

    if (userIndex !== -1) {
      state[userIndex] = user;
      return [...state];
    } else {
      return [user, ...state];
    }
  }

  if (action.type === "DELETE_USER") {
    const userId = action.payload;

    const userIndex = state.findIndex((u) => u.id === userId);
    if (userIndex !== -1) {
      state.splice(userIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => {
  const ds = theme.chatiaDS || {};
  const dsColors = ds.colors || {};
  const isDark = theme.palette.type === "dark";
  return {
    pageHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      flexWrap: "wrap",
      padding: "4px 4px 14px",
    },
    headerLeft: {
      display: "flex",
      alignItems: "center",
      gap: 12,
    },
    pageIconWrap: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: 36,
      height: 36,
      borderRadius: 10,
      background: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
      color: theme.palette.primary.main,
    },
    pageTitle: {
      fontSize: 18,
      fontWeight: 600,
      color: dsColors.textPrimary || (isDark ? "#e9edef" : "#1a1a1a"),
      lineHeight: 1.2,
      display: "inline-flex",
      alignItems: "center",
      gap: 10,
      margin: 0,
    },
    pageSubtitle: {
      fontSize: 12,
      color:
        dsColors.textSecondary ||
        (isDark ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.55)"),
      marginTop: 2,
    },
    pageCountChip: {
      display: "inline-flex",
      alignItems: "center",
      padding: "2px 9px",
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 700,
      letterSpacing: 0.2,
      background: isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)",
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
    },
    pageActions: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      gap: 8,
      flexWrap: "wrap",
    },
    mainPaper: {
      flex: 1,
      minHeight: 0,
      padding: 0,
      overflow: "hidden",
      borderRadius: 12,
      background: dsColors.surfaceDefault || (isDark ? "#11141b" : "#f7f8fa"),
      border: `1px solid ${dsColors.border || (isDark ? "#202632" : "#e8e8e8")}`,
      boxShadow: isDark
        ? "0 2px 12px rgba(0,0,0,0.35)"
        : "0 2px 12px rgba(15,23,42,0.06)",
      display: "flex",
      flexDirection: "column",
    },
    tableScroll: {
      flex: 1,
      minHeight: 0,
      height: "100%",
      maxHeight: "100%",
      overflowY: "auto",
      overflowX: "auto",
      ...theme.scrollbarStyles,
    },
    table: {
      borderCollapse: "separate",
      borderSpacing: 0,
      minWidth: 980,
      "& .MuiTableCell-root": {
        borderBottom: `1px solid ${dsColors.borderLight || (isDark ? "#1a1f29" : "#f0f0f0")}`,
        color: dsColors.textPrimary || (isDark ? "#e9edef" : "#111b21"),
        fontSize: 13.5,
        padding: "14px 16px",
      },
    },
    tableHead: {
      "& .MuiTableCell-head": {
        background: dsColors.surfaceDefault || (isDark ? "#11141b" : "#f7f8fa"),
        backgroundColor: dsColors.surfaceDefault || (isDark ? "#11141b" : "#f7f8fa"),
        color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
        fontWeight: 700,
        fontSize: 11.5,
        textTransform: "uppercase",
        letterSpacing: 0.6,
        borderBottom: `1px solid ${dsColors.border || (isDark ? "#202632" : "#e8e8e8")}`,
        padding: "12px 16px",
        position: "sticky",
        top: 0,
        zIndex: 3,
      },
    },
    tableRow: {
      transition: "background-color 0.18s ease",
      "&:hover": {
        backgroundColor: dsColors.surfaceHover || (isDark ? "#171b24" : "#f3f4f6"),
      },
      "&:last-child .MuiTableCell-root": {
        borderBottom: "none",
      },
    },
    nameCell: {
      fontWeight: 600,
      color: dsColors.textPrimary || (isDark ? "#e9edef" : "#111b21"),
    },
    emailCell: {
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
      fontSize: 13,
    },
    profileChip: {
      display: "inline-flex",
      alignItems: "center",
      padding: "3px 10px",
      borderRadius: 999,
      fontSize: 11.5,
      fontWeight: 700,
      letterSpacing: 0.3,
      textTransform: "uppercase",
    },
    profileAdmin: {
      background: isDark ? "rgba(76,175,80,0.14)" : "rgba(76,175,80,0.12)",
      color: isDark ? "#a5d6a7" : "#2e7d32",
    },
    profileUser: {
      background: isDark ? "rgba(33,150,243,0.14)" : "rgba(33,150,243,0.10)",
      color: isDark ? "#90caf9" : "#1565c0",
    },
    avatarDiv: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    userAvatar: {
      width: theme.spacing(5),
      height: theme.spacing(5),
      border: `1px solid ${dsColors.border || (isDark ? "#202632" : "#e8e8e8")}`,
    },
    timePill: {
      display: "inline-block",
      padding: "3px 9px",
      borderRadius: 8,
      fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
      fontSize: 12.5,
      background: isDark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.04)",
      color: dsColors.textPrimary || (isDark ? "#e9edef" : "#111b21"),
    },
    iconActionBtn: {
      borderRadius: 10,
      padding: 6,
      transition: "all 0.18s ease",
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
      "&:hover": {
        background: isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)",
        color: dsColors.textPrimary || (isDark ? "#e9edef" : "#111b21"),
      },
    },
    deleteActionBtn: {
      borderRadius: 10,
      padding: 6,
      transition: "all 0.18s ease",
      color: dsColors.danger || "#e53935",
      "&:hover": {
        background: dsColors.dangerBg || "rgba(229,57,53,0.10)",
        color: dsColors.dangerHover || "#c62828",
      },
    },
    actionsCell: {
      whiteSpace: "nowrap",
    },
    emptyState: {
      padding: "60px 16px",
      textAlign: "center",
      color: dsColors.textMuted || (isDark ? "#666" : "#999"),
      fontSize: 13.5,
    },
    loadingContainer: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      padding: theme.spacing(3),
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
    },
    loadingText: {
      marginLeft: theme.spacing(2),
      fontSize: 13.5,
    },
  };
});

const Users = () => {
  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const {
    ticketSearchParam: searchParam,
    setTicketSearchParam: setSearchParam,
  } = useSearch();
  const [users, dispatch] = useReducer(reducer, []);
  const { user: loggedInUser, socket } = useContext(AuthContext);
  const { profileImage } = loggedInUser;

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const fetchUsers = async () => {
      try {
        const { data } = await api.get("/users/", {
          params: { searchParam, pageNumber },
        });
        dispatch({ type: "LOAD_USERS", payload: data.users });
        setHasMore(data.hasMore);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    };
    fetchUsers();
  }, [searchParam, pageNumber]);

  useEffect(() => {
    if (loggedInUser) {
      const companyId = loggedInUser.companyId;
      const onCompanyUser = (data) => {
        if (data.action === "update" || data.action === "create") {
          dispatch({ type: "UPDATE_USERS", payload: data.user });
        }
        if (data.action === "delete") {
          dispatch({ type: "DELETE_USER", payload: +data.userId });
        }
      };
      socket.on(`company-${companyId}-user`, onCompanyUser);
      return () => {
        socket.off(`company-${companyId}-user`, onCompanyUser);
      };
    }
  }, [socket]);

  const handleOpenUserModal = () => {
    setSelectedUser(null);
    setUserModalOpen(true);
  };

  const handleCloseUserModal = () => {
    setSelectedUser(null);
    setUserModalOpen(false);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setUserModalOpen(true);
  };

  const handleDeleteUser = async (userId) => {
    try {
      await api.delete(`/users/${userId}`);
      dispatch({ type: "DELETE_USER", payload: +userId });
      toast.success(i18n.t("users.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingUser(null);
  };

  const loadMore = () => {
    setLoadingMore(true);
    setPageNumber((prevPage) => prevPage + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const maskEmail = (email) => {
    if (!email || typeof email !== "string") return "";
    const at = email.indexOf("@");
    if (at <= 0) return email;
    const local = email.slice(0, at);
    const domain = email.slice(at);
    if (local.length <= 2) {
      return `${local[0]}***${domain}`;
    }
    const visibleStart = local.slice(0, 2);
    const visibleEnd = local.length > 4 ? local.slice(-1) : "";
    return `${visibleStart}***${visibleEnd}${domain}`;
  };

  const resolveAvatarUrl = (img) => {
    if (!img) return whatsappIcon;
    if (/^https?:\/\//i.test(img)) return img;
    return `${backendUrl}/public/avatar/${img}`;
  };

  const renderProfileImage = (user) => {
    const img =
      user.id === loggedInUser.id ? profileImage || user.profileImage : user.profileImage;
    return (
      <Avatar
        src={resolveAvatarUrl(img)}
        alt={user.name}
        className={classes.userAvatar}
      />
    );
  };

  const renderProfileChip = (profile) => {
    const isAdmin = profile === "admin";
    return (
      <span
        className={`${classes.profileChip} ${isAdmin ? classes.profileAdmin : classes.profileUser}`}
      >
        {isAdmin ? i18n.t("users.profile.admin") : i18n.t("users.profile.user")}
      </span>
    );
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          deletingUser &&
          `${i18n.t("users.confirmationModal.deleteTitle")} ${deletingUser.name}?`
        }
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={() => handleDeleteUser(deletingUser.id)}
      >
        {i18n.t("users.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <UserModal
        open={userModalOpen}
        onClose={handleCloseUserModal}
        onSaved={(savedUser) =>
          dispatch({ type: "UPDATE_USERS", payload: savedUser })
        }
        aria-labelledby="form-dialog-title"
        userId={selectedUser && selectedUser.id}
        key={i18n.language}
      />
      {loggedInUser.profile === "user" ? (
        <ForbiddenPage />
      ) : (
        <>
          <div className={classes.pageHeader}>
            <div className={classes.headerLeft}>
              <span className={classes.pageIconWrap}>
                <UsersIcon size={20} strokeWidth={1.8} />
              </span>
              <div>
                <h1 className={classes.pageTitle}>
                  {i18n.t("users.title")}
                  <span className={classes.pageCountChip}>{users.length}</span>
                </h1>
                <div className={classes.pageSubtitle}>
                  {i18n.t("users.subtitle")}
                </div>
              </div>
            </div>
            <div className={classes.pageActions}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleOpenUserModal}
              >
                {i18n.t("users.buttons.add")}
              </Button>
            </div>
          </div>

          <Paper
            className={classes.mainPaper}
            variant="outlined"
            elevation={0}
          >
            <div className={classes.tableScroll} onScroll={handleScroll}>
              <Table size="small" stickyHeader className={classes.table}>
                <TableHead className={classes.tableHead}>
                  <TableRow>
                    <TableCell align="center" style={{ width: 70 }}>
                      {i18n.t("users.table.ID")}
                    </TableCell>
                    <TableCell align="center" style={{ width: 160 }}>
                      {i18n.t("users.table.status")}
                    </TableCell>
                    <TableCell align="center" style={{ width: 80 }}>
                      {i18n.t("users.table.avatar")}
                    </TableCell>
                    <TableCell align="left">
                      {i18n.t("users.table.name")}
                    </TableCell>
                    <TableCell align="left">
                      {i18n.t("users.table.email")}
                    </TableCell>
                    <TableCell align="center" style={{ width: 110 }}>
                      {i18n.t("users.table.profile")}
                    </TableCell>
                    <TableCell align="center" style={{ width: 130 }}>
                      {i18n.t("users.table.startWork")}
                    </TableCell>
                    <TableCell align="center" style={{ width: 130 }}>
                      {i18n.t("users.table.endWork")}
                    </TableCell>
                    <TableCell align="center" style={{ width: 110 }}>
                      {i18n.t("users.table.actions")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.length > 0
                    ? users.map((user) => (
                        <TableRow
                          key={user.id}
                          className={classes.tableRow}
                          hover
                        >
                          <TableCell align="center">{user.id}</TableCell>
                          <TableCell align="center">
                            <UserStatusIcon user={user} />
                          </TableCell>
                          <TableCell align="center">
                            <div className={classes.avatarDiv}>
                              {renderProfileImage(user)}
                            </div>
                          </TableCell>
                          <TableCell align="left" className={classes.nameCell}>
                            {user.name}
                          </TableCell>
                          <TableCell align="left" className={classes.emailCell}>
                            <Tooltip title={user.email || ""}>
                              <span>{maskEmail(user.email)}</span>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="center">
                            {renderProfileChip(user.profile)}
                          </TableCell>
                          <TableCell align="center">
                            <span className={classes.timePill}>
                              {user.startWork || "—"}
                            </span>
                          </TableCell>
                          <TableCell align="center">
                            <span className={classes.timePill}>
                              {user.endWork || "—"}
                            </span>
                          </TableCell>
                          <TableCell
                            align="center"
                            className={classes.actionsCell}
                          >
                            <Tooltip title={i18n.t("users.buttons.edit")}>
                              <IconButton
                                size="small"
                                className={classes.iconActionBtn}
                                onClick={() => handleEditUser(user)}
                              >
                                <Pencil size={16} strokeWidth={2} />
                              </IconButton>
                            </Tooltip>
                            {user.id !== 1 &&
                              user.email !== "admin@admin.com" && (
                                <Tooltip title={i18n.t("users.buttons.delete")}>
                                  <IconButton
                                    size="small"
                                    className={classes.deleteActionBtn}
                                    onClick={() => {
                                      setConfirmModalOpen(true);
                                      setDeletingUser(user);
                                    }}
                                  >
                                    <Trash2 size={16} strokeWidth={2} />
                                  </IconButton>
                                </Tooltip>
                              )}
                          </TableCell>
                        </TableRow>
                      ))
                    : !loading && (
                        <TableRow>
                          <TableCell
                            colSpan={9}
                            className={classes.emptyState}
                          >
                            {i18n.t("users.empty")}
                          </TableCell>
                        </TableRow>
                      )}
                  {loadingMore && (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        <CircularProgress size={20} />
                      </TableCell>
                    </TableRow>
                  )}
                  {loading && !loadingMore && (
                    <TableRowSkeleton columns={9} />
                  )}
                </TableBody>
              </Table>
            </div>
          </Paper>
        </>
      )}
    </MainContainer>
  );
};

export default Users;
